import { type OptionProps, SelectBox } from "@root/shared/components/SelectBox";
import { LazyLoadImage } from "@root/shared/components/LazyLoadImage";
import { AreaModal } from "./AreaModal";
import { SelectedChoices } from "./SelectedChoices";
import * as styles from "./styles.css";
import type { ChoiceGroup, Choice } from "../../types";

export type AreaSelectorProps = {
  prefectureValue: string;
  onPrefectureChange: (value: string) => void;
  prefectureOptions: OptionProps[];
  cityValues: string[];
  onCityChange: (values: string[]) => void;
  cityChoices: ChoiceGroup[];
  lineValues: string[];
  onLineChange: (values: string[]) => void;
  stationValues: string[];
  onStationChange: (values: string[]) => void;
  lineChoices: ChoiceGroup[];
  popularAreaValues: string[];
  onPopularAreaChange: (values: string[]) => void;
  popularAreaChoices: Choice[];
  isActionButtonDisabled: boolean;
  appDlUrl: string;
  appImgSrc: string;
  appGaLabel: string;
  isLoggedIn: boolean;
  isSp: boolean;
};

export const AreaSelector = (props: AreaSelectorProps) => {
  const {
    prefectureValue,
    onPrefectureChange,
    prefectureOptions,
    cityValues,
    onCityChange,
    cityChoices,
    lineValues,
    onLineChange,
    stationValues,
    onStationChange,
    lineChoices,
    popularAreaValues,
    onPopularAreaChange,
    popularAreaChoices,
    isActionButtonDisabled,
    appDlUrl,
    appImgSrc,
    appGaLabel,
    isLoggedIn,
    isSp,
  } = props;

  const handlePrefectureChange = (value: string) => {
    onPrefectureChange(value);
    onCityChange([]);
    onLineChange([]);
    onStationChange([]);
    onPopularAreaChange([]);
  };

  const handleLineChange = (values: string[]) => {
    const lineValueSet = new Set(lineChoices.map((group) => group.value));
    const newLineValues = values.filter((value) => lineValueSet.has(value));
    onLineChange(newLineValues);
    const stationValueSet = new Set(
      lineChoices.flatMap((group) => group.choices.map((choice) => choice.value)),
    );
    const newStationValues = values.filter((value) => stationValueSet.has(value));
    onStationChange(newStationValues);
  };

  return (
    <div className={styles.wrapper}>
      <SelectedChoices
        popularAreaValues={popularAreaValues}
        onPopularAreaRemove={onPopularAreaChange}
        popularAreaChoices={popularAreaChoices}
        cityValues={cityValues}
        onCityRemove={onCityChange}
        cityChoices={cityChoices}
        lineValues={[...lineValues, ...stationValues]}
        onLineRemove={handleLineChange}
        lineChoices={lineChoices}
      />
      <SelectBox
        selected={prefectureValue !== undefined}
        value={prefectureValue}
        onChange={(e) => handlePrefectureChange(e.target.value)}
        options={prefectureOptions}
        placeholder="都道府県を選択してください"
        aria-label="都道府県を選択"
      />
      <div className={styles.areaModalsWrapper}>
        <AreaModal
          isActionButtonDisabled={isActionButtonDisabled}
          title="市区町村を選択"
          values={cityValues}
          onChange={onCityChange}
          choiceGroups={cityChoices}
          popularAreaValues={popularAreaValues}
          onPopularAreaChange={onPopularAreaChange}
          popularAreaChoices={popularAreaChoices}
          defaultIsOpened
        />
        <AreaModal
          isActionButtonDisabled={isActionButtonDisabled}
          title="駅・路線を選択"
          values={[...lineValues, ...stationValues]}
          onChange={handleLineChange}
          choiceGroups={lineChoices}
          popularAreaValues={popularAreaValues}
          onPopularAreaChange={onPopularAreaChange}
          popularAreaChoices={popularAreaChoices}
          defaultIsOpened={false}
        />
      </div>
      {isSp && (
        <a
          href={appDlUrl}
          data-is-login={isLoggedIn}
          data-ga-action="app_promotion"
          data-ga-label={appGaLabel}
          className={styles.imgWrapper}
        >
          <LazyLoadImage
            src={appImgSrc}
            alt="アプリの地図検索なら周辺店舗がひとめでわかる！ ダウンロードする"
            data-ga-action="app_promotion"
            data-ga-label={appGaLabel}
            height={104}
            width={320}
          />
        </a>
      )}
    </div>
  );
};
